import { useState } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { API, apiError, fmtUSD } from "@/lib/auth";

const KEY = "montalban_admin_key";
const TABS = [
  ["bidders", "Postores"],
  ["bids", "Pujas"],
  ["settings", "Ajustes"],
  ["leads", "Solicitudes"],
  ["donations", "Donaciones"],
];
const PATHS = {
  bidders: "/auction/admin/bidders",
  bids: "/auction/admin/bids",
  settings: "/auction/admin/settings",
  leads: "/leads",
  donations: "/donations",
};

const Th = ({ children }) => (
  <th className="px-3 py-2 text-left font-code text-[10px] uppercase tracking-[0.2em] text-slate-500">{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 text-sm text-slate-200 ${className}`}>{children}</td>;

function Settings({ data, headers, refresh }) {
  const [f, setF] = useState({
    ends_at: data.ends_at,
    starting_bid: data.starting_bid,
    min_increment: data.min_increment,
    extension_minutes: data.extension_minutes,
  });
  const save = async () => {
    try {
      await axios.put(`${API}/auction/admin/settings`, { ...f, starting_bid: +f.starting_bid, min_increment: +f.min_increment, extension_minutes: +f.extension_minutes }, { headers });
      toast.success("Ajustes guardados");
      refresh();
    } catch (e) {
      toast.error(apiError(e, "Error"));
    }
  };
  const inp = "mt-1 w-full border border-white/15 bg-[#0A0C10] px-3 py-2 text-sm text-white outline-none focus:border-amber-400";
  return (
    <div data-testid="admin-settings" className="grid max-w-2xl gap-4 sm:grid-cols-2">
      {[
        ["ends_at", "Cierre (ISO, UTC) — 18:00 VET = 22:00Z", "text"],
        ["starting_bid", "Puja inicial mínima (USD)", "number"],
        ["min_increment", "Incremento mínimo (USD)", "number"],
        ["extension_minutes", "Extensión anti-cierre (min)", "number"],
      ].map(([k, l, type]) => (
        <label key={k} className="block text-xs text-slate-400">
          {l}
          <input data-testid={`admin-setting-${k}`} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp} />
        </label>
      ))}
      <button data-testid="admin-settings-save" onClick={save} className="bg-amber-400 px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-[#0A0C10] hover:bg-amber-300 sm:col-span-2">
        Guardar ajustes
      </button>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem(KEY) || "");
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("bidders");
  const qc = useQueryClient();
  const headers = { "X-Admin-Key": key };

  const { data, error, isLoading } = useQuery({
    queryKey: ["admin", tab, key],
    queryFn: async () => (await axios.get(`${API}${PATHS[tab]}`, { headers })).data,
    enabled: !!key,
    retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin"] });

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/auction/admin/bidders/${id}`, { status }, { headers });
      toast.success(`Postor ${status === "approved" ? "aprobado" : status === "rejected" ? "rechazado" : "en pendiente"}`);
      refresh();
    } catch (e) {
      toast.error(apiError(e, "Error"));
    }
  };

  if (!key) {
    return (
      <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-[#0A0C10] px-6 text-slate-100">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sessionStorage.setItem(KEY, input);
            setKey(input);
          }}
          className="w-full max-w-sm border border-white/10 bg-[#0E1119] p-8"
        >
          <ShieldCheck className="text-amber-400" />
          <h1 className="mt-4 font-display text-2xl font-bold uppercase text-white">Mesa Técnica</h1>
          <input
            data-testid="admin-key-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Clave de administración"
            className="mt-6 w-full border border-white/15 bg-[#0A0C10] px-4 py-3 text-sm text-white outline-none focus:border-amber-400"
          />
          <button data-testid="admin-key-submit" className="mt-4 w-full bg-amber-400 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-[#0A0C10]">
            Entrar
          </button>
        </form>
      </div>
    );
  }

  if (error?.response?.status === 401) {
    sessionStorage.removeItem(KEY);
    setKey("");
    toast.error("Clave inválida");
  }

  const statusColor = { pending: "text-amber-400", approved: "text-emerald-400", rejected: "text-red-400" };

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#0A0C10] font-body text-slate-100">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 font-code text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-amber-400">
            <ArrowLeft size={14} /> Sitio
          </Link>
          <span className="font-display text-sm font-bold uppercase tracking-wide text-white">Mesa Técnica · Panel</span>
          <button
            data-testid="admin-logout"
            onClick={() => {
              sessionStorage.removeItem(KEY);
              setKey("");
            }}
            className="font-code text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-amber-400"
          >
            Salir
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap gap-1 border-b border-white/10">
          {TABS.map(([k, l]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-4 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] ${tab === k ? "border-b-2 border-amber-400 text-amber-400" : "text-slate-400 hover:text-white"}`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="mt-8 overflow-x-auto">
          {isLoading && <p className="text-sm text-slate-500">Cargando…</p>}
          {tab === "settings" && data && <Settings data={data} headers={headers} refresh={refresh} />}

          {tab === "bidders" && Array.isArray(data) && (
            <table data-testid="admin-bidders-table" className="w-full border border-white/10">
              <thead className="bg-[#0E1119]"><tr><Th>Nombre</Th><Th>Empresa</Th><Th>Correo</Th><Th>Teléfono</Th><Th>Alias</Th><Th>Muestra</Th><Th>Estado</Th><Th>Acciones</Th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {data.map((b) => (
                  <tr key={b.id} data-testid={`admin-bidder-${b.email}`}>
                    <Td>{b.name}</Td><Td>{b.company || "—"}</Td><Td>{b.email}</Td><Td>{b.phone}</Td><Td>{b.alias}</Td>
                    <Td>{b.display_mode === "name" ? "Nombre" : "Alias"}</Td>
                    <Td className={`font-code text-xs uppercase ${statusColor[b.status]}`}>{b.status}</Td>
                    <Td>
                      <div className="flex gap-2">
                        {b.status !== "approved" && <button data-testid={`approve-${b.email}`} onClick={() => setStatus(b.id, "approved")} className="border border-emerald-400/50 px-2 py-1 text-xs text-emerald-400 hover:bg-emerald-400/10">Aprobar</button>}
                        {b.status !== "rejected" && <button data-testid={`reject-${b.email}`} onClick={() => setStatus(b.id, "rejected")} className="border border-red-400/50 px-2 py-1 text-xs text-red-400 hover:bg-red-400/10">Rechazar</button>}
                      </div>
                    </Td>
                  </tr>
                ))}
                {data.length === 0 && <tr><Td>Sin postores registrados</Td></tr>}
              </tbody>
            </table>
          )}

          {tab === "bids" && Array.isArray(data) && (
            <table data-testid="admin-bids-table" className="w-full border border-white/10">
              <thead className="bg-[#0E1119]"><tr><Th>Fecha (UTC)</Th><Th>Monto</Th><Th>Postor</Th><Th>Empresa</Th><Th>Correo</Th><Th>Teléfono</Th><Th>Alias</Th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {data.map((x) => (
                  <tr key={x.id}>
                    <Td className="font-code text-xs">{x.created_at.slice(0, 19).replace("T", " ")}</Td>
                    <Td className="font-display font-bold text-amber-400">{fmtUSD(x.amount)}</Td>
                    <Td>{x.bidder.name}</Td><Td>{x.bidder.company || "—"}</Td><Td>{x.bidder.email}</Td><Td>{x.bidder.phone}</Td><Td>{x.bidder.alias}</Td>
                  </tr>
                ))}
                {data.length === 0 && <tr><Td>Sin pujas</Td></tr>}
              </tbody>
            </table>
          )}

          {tab === "leads" && Array.isArray(data) && (
            <table data-testid="admin-leads-table" className="w-full border border-white/10">
              <thead className="bg-[#0E1119]"><tr><Th>Fecha</Th><Th>Representante</Th><Th>Empresa</Th><Th>Correo</Th><Th>Teléfono</Th><Th>Interés</Th><Th>Mensaje</Th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {data.map((l) => (
                  <tr key={l.id}>
                    <Td className="font-code text-xs">{l.created_at.slice(0, 10)}</Td><Td>{l.representative_name}</Td><Td>{l.company_name}</Td><Td>{l.corporate_email}</Td><Td>{l.phone_whatsapp}</Td><Td>{l.interest_area}</Td><Td className="max-w-xs truncate">{l.message || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "donations" && Array.isArray(data) && (
            <table data-testid="admin-donations-table" className="w-full border border-white/10">
              <thead className="bg-[#0E1119]"><tr><Th>Fecha</Th><Th>Monto</Th><Th>Tipo</Th><Th>Donante</Th><Th>Correo</Th><Th>Anónimo</Th><Th>Estado</Th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {data.map((d) => (
                  <tr key={d.session_id}>
                    <Td className="font-code text-xs">{d.created_at.slice(0, 10)}</Td><Td className="font-display font-bold text-amber-400">{fmtUSD(d.amount)}</Td><Td>{d.interval === "month" ? "Mensual" : "Única"}</Td><Td>{d.donor_name || "—"}</Td><Td>{d.donor_email || "—"}</Td><Td>{d.anonymous ? "Sí" : "No"}</Td>
                    <Td className={`font-code text-xs uppercase ${d.payment_status === "paid" ? "text-emerald-400" : "text-slate-500"}`}>{d.payment_status}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
